import type {
  ConfigurationFacet,
  PriceFacet,
} from '@backmarket/nuxt-layer-recommendation/filtering/types'
import type { I18nDefinition } from '@backmarket/nuxt-module-i18n/types/i18n'

import translations from './constants.translations'

export type OutletCategorySlug = 'iphone' | 'macbook'

export const OUTLET_PRICE_FACET: PriceFacet = {
  name: 'price',
  title: 'Price',
  price_facet_type: 'chart',
  scales: [
    { maxValue: 100, facetName: 'price_ranges.sm-1' },
    { maxValue: 500, facetName: 'price_ranges.sm-2' },
    { maxValue: 1000, facetName: 'price_ranges.md-1' },
    { maxValue: 1500, facetName: 'price_ranges.md-1b' },
    { maxValue: 2000, facetName: 'price_ranges.md-1c' },
    { maxValue: 2500, facetName: 'price_ranges.md-2' },
    { maxValue: 5000, facetName: 'price_ranges.lg-1' },
  ],
}

export const HITS_PER_PAGE = 32

// Landing page card types and data
export type OutletCardState = 'in-stock' | 'upcoming'

export type OutletLandingCard = {
  id: string
  label: I18nDefinition
  slug: OutletCategorySlug | null
  state: OutletCardState
  // Static product image used for cards without a live Algolia preview.
  imageUrl?: string
  // Screensaver animation: when both are set the card starts with the
  // no-screensaver (black screen) image and cross-fades to the screensaver
  // (lit screen) image after `screensaverDelay` ms, giving the impression
  // the device is turning on.
  screensaverImage?: string
  noScreensaverImage?: string
  screensaverDelay?: number
  screensaverPadding?: number
  // Category metadata: Algolia filter and facets (only present for cards with a category slug)
  filter?: string
  facets?: ConfigurationFacet[]
  // Counter dates: startDate for when the offer begins, endDate for countdown (ISO 8601 format)
  startDate?: string
  endDate?: string
  introImage?: string
  // Default product grid column count on mobile for this category
  // (tablet/desktop remain at md:grid-cols-3 lg:grid-cols-4). When omitted
  // the grid defaults to 2 columns.
  defaultColumns?: 1 | 2
  // Locale-specific filter appended to `filter` (e.g. keyboard layout per
  // market). Keyed by locale code; value is the raw Algolia filter fragment.
  localeFilters?: Record<string, string>
}

export const OUTLET_LANDING_CARDS: readonly OutletLandingCard[] = [
  {
    id: 'ugly-iphones',
    label: translations.cardLabelUglyIphones,
    slug: 'iphone',
    state: 'in-stock',
    // Start with blank screen, light up at 500 ms (iPhone turns on first).
    noScreensaverImage: '/img/outlet/iphone-outlet-no-screensaver.png',
    screensaverImage: '/img/outlet/iphone-outlet-screensaver.png',
    screensaverDelay: 500,
    screensaverPadding: 20,
    filter: '(cat_id:"2") AND (brand_clean:"apple")',
    facets: [
      {
        name: 'model',
        title: 'Model',
        type: 'checkbox',
        isSortedByBusiness: true,
      },
      { name: 'screen_size', title: 'Screen size', type: 'checkbox' },
      { name: 'storage', title: 'Storage', type: 'checkbox' },
      { name: 'color', title: 'Color', type: 'checkbox' },
      {
        name: 'year_date_release',
        title: 'Release year',
        type: 'checkbox',
        isSortedByBusiness: true,
      },
    ],
    introImage: '/img/outlet/iphone-outlet-screensaver.png',
  },
  {
    id: 'ugly-macbooks',
    label: translations.cardLabelUglyMacbooks,
    slug: 'macbook',
    state: 'in-stock',
    // MacBook turns on 700 ms after the iPhone (1.2 s total).
    noScreensaverImage: '/img/outlet/macbook-outlet-no-screensaver.png',
    screensaverImage: '/img/outlet/macbook-outlet-screensaver.png',
    screensaverDelay: 1200,
    filter: '(cat_id:"528")',
    defaultColumns: 1,
    localeFilters: {
      'fr-fr': ' AND (keyboard_type_language:"AZERTY - Français")',
      'en-gb': ' AND (keyboard_type_language:"QWERTY - English")',
    },
    facets: [
      {
        name: 'model',
        title: 'Model',
        type: 'checkbox',
        isSortedByBusiness: true,
      },
      { name: 'screen_size', title: 'Screen size', type: 'checkbox' },
      { name: 'processor_type', title: 'Processor', type: 'checkbox' },
      { name: 'memory', title: 'Memory', type: 'checkbox' },
      { name: 'storage', title: 'Storage', type: 'checkbox' },
      { name: 'color', title: 'Color', type: 'checkbox' },
      {
        name: 'year_date_release',
        title: 'Release year',
        type: 'checkbox',
        isSortedByBusiness: true,
      },
      {
        name: 'keyboard_type_language',
        title: 'Keyboard language',
        type: 'checkbox',
      },
    ],
    introImage: '/img/outlet/macbook-outlet-screensaver.png',
  },
] as const

export const OUTLET_MODAL_NAMES = {
  REASSURANCE: 'OUTLET_REASSURANCE',
  LOOKS_UGLY: 'OUTLET_LOOKS_UGLY',
  BATTERY: 'OUTLET_BATTERY',
} as const
